import React, { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Option, OptionType } from '../../types'
import Button from '../Button/Button'
import Select from '../Select/Select'
import TextField from '../TextField/TextField'
import { SessionState } from 'reducers/session'
import { RefreshToast } from '../RefreshToast/RefreshToast'
import config from '../../config'
import './Login.scss'
import { useAvailableLocaleOptions, useI18nSelector, useI18nDispatch, useI18nState } from '../../v3/context/i18n'
import { resetDateFilters, setLocalStorage } from '../../util'
import { i18nActionTypes } from '../../actions'
import { doRequest } from '../../middleware'
import { AuthActionTypes } from '../../actions/auth'
import { getLoginError, LoginError } from './LoginError'
import { LoginErrorMessage } from './LoginError'
import { isSessionValid } from '../../util/session'
import { datadogLogs } from '@datadog/browser-logs'

type RootLoginProps = {
  email?: string
  error?: string
}
export const RootLogin = ({ email: propsEmail, error: propsError }: RootLoginProps) => {
  const [email, setEmail] = useState<string>(propsEmail ?? '')
  const [password, setPassword] = useState('')
  let initErr: LoginError | null = null
  if (propsError) {
    initErr = {
      root: [propsError],
    }
  }
  const [error, setError] = useState<LoginError | null>(initErr)
  const history = useHistory()
  const { locale } = useI18nState()
  const availableLocales = useAvailableLocaleOptions()
  const i18n = useI18nSelector()
  const i18nDispatch = useI18nDispatch()
  const location = useLocation()

  let { from } = location.state || { from: { pathname: '/' } }
  if (!from || !from.pathname || from.pathname === '/login') {
    from = {
      pathname: '/',
    }
  }

  const onSubmit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()

    datadogLogs.setUser({ id: email })
    doRequest({
      url: `${config.api}/authenticate/credentials`,
      method: 'POST',
      headers: {
        'Accept-Language': locale,
      },
      payload: {
        email,
        password,
      },
      validateStatus: (status) => {
        return status === 200
      },
      type: AuthActionTypes.Login,
    })
      .then((res) => {
        if (!res) return
        const data = res.data as SessionState
        if (!isSessionValid(data)) {
          throw new Error('session data from credentials login was not valid')
        }
        return data
      })
      .then((data) => {
        if (!data) return
        setLocalStorage(config.sessionKey, data as SessionState)
        window.dispatchEvent(new Event('toggle-theme'))
        // AB#8587 Lead list date filters should be reset on every user session
        resetDateFilters()
        // reloading the window; this allows the app to re-fetch the session from storage and load into desktop or mobile views
        history.replace(from)
      })
      .catch((err) => {
        setError(getLoginError(i18n, err, AuthActionTypes.Login))
      })
  }

  const logoClasses = ['logo']
  if (error) {
    logoClasses.push('has-error')
  }

  return (
    <div className="LoginScreen">
      <div className="Login">
        <form className="form">
          <div className={logoClasses.join(' ')} />
          <LoginErrorMessage error={error} />
          <TextField
            label={i18n('auth.emailLabel')}
            onInput={(value: string) => {
              setError(null)
              setEmail(value)
            }}
            type="email"
            value={email}
            error={error?.inputs?.email}
            testID={'login-email'}
          />
          <TextField
            label={i18n('auth.passwordLabel')}
            onInput={(value: string) => {
              setError(null)
              setPassword(value)
            }}
            type="password"
            value={password}
            error={error?.inputs?.password}
            testID={'login-password'}
          />
          <Select
            dataCIKey={'locale'}
            label={i18n('locale.language')}
            options={availableLocales}
            value={availableLocales.filter((v: any): boolean => locale === v.value)}
            onChange={(opts: Option[]) => {
              const locale = opts[0]
              if (locale.type !== OptionType.Enum) return
              i18nDispatch({ type: i18nActionTypes.SET_LOCALE, locale: locale.value })
            }}
          />
          <div className="buttons">
            <div className="actions login">
              <Button className="submit" id="submit" variant="contained" color="primary" disableElevation onClick={onSubmit} type="submit">
                {i18n('auth.cta.signIn')}
              </Button>
            </div>
            <div className="helpers">
              <Button className="forgotPassword" onClick={() => history.push('/forgot-password')}>
                {i18n('auth.forgotPassword')}
              </Button>
              <LoginHelpButton reqType={AuthActionTypes.Login} />
            </div>
          </div>
        </form>
        <div className={'recaptcha-disclaimer'}>
          <p
            dangerouslySetInnerHTML={{
              __html: i18n('auth.recaptcha.disclaimer'),
            }}
          ></p>
        </div>
      </div>
      <RefreshToast />
    </div>
  )
}

type LoginHelpButtonProps = {
  reqType: string
}
export const LoginHelpButton = ({ reqType }: LoginHelpButtonProps) => {
  const i18n = useI18nSelector()
  let link = 'https://powerchord.atlassian.net/servicedesk/customer/portals'
  if (reqType !== AuthActionTypes.Login) {
    link = 'https://powerchord.atlassian.net/servicedesk/customer/kb/view/723222633'
  }
  return (
    <Button className="help">
      <a href={link} target={'_blank'} rel={'noreferrer'}>
        {i18n('auth.needHelp')}
      </a>
    </Button>
  )
}
